import { useState } from 'react';
import { Filter, SearchInput, SelectInput, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Avatar, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { Balance, Gavel } from '@mui/icons-material';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { ListPageHeader } from '@/components/ListPageHeader';
import { LIABILITY_STATUS, LIABILITY_STATUS_COLORS } from './constants';
import type { LiabilityRecord, TESVendorRecord } from '@/types/entities';

export const LiabilityRecordList = () => {
  const navigate = useNavigate();
  const employeeMap = useEmployeeMap();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: records, isLoading } = useGetList('liability-records', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const { data: allVendors } = useGetList('tes-vendors', { pagination: { page: 1, perPage: 1000 } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const vendorMap = new Map((allVendors ?? []).map((v: TESVendorRecord) => [v.id, v]));

  const resolveVendorName = (id?: string) => {
    if (!id) return '—';
    const vendor = vendorMap.get(id);
    return vendor?.legalEntity?.tradingName || vendor?.vendorName || vendor?.legalEntity?.companyName || id;
  };

  const stats = {
    total: records?.length ?? 0,
    active: records?.filter((r: LiabilityRecord) => r.status === 'Active').length ?? 0,
    pending: records?.filter((r: LiabilityRecord) => r.status === 'Pending').length ?? 0,
    disputed: records?.filter((r: LiabilityRecord) => r.status === 'Disputed').length ?? 0,
    resolved: records?.filter((r: LiabilityRecord) => r.status === 'Resolved' || r.status === 'Expired').length ?? 0,
  };

  const statusCounts = records?.reduce((acc: Record<string, number>, r: LiabilityRecord) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(statusCounts).map(([label, value]) => ({ label, value: Number(value) }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Liability Records"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'warning.main' },
            { label: 'Pending', value: String(stats.pending), color: 'info.main' },
            { label: 'Disputed', value: String(stats.disputed), color: 'error.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {records?.map((record: LiabilityRecord) => (
            <Paper key={record.id} sx={{ p: 2 }} onClick={() => navigate(`/liability-records/${record.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: LIABILITY_STATUS_COLORS[record.status] === 'error' ? 'error.main' : LIABILITY_STATUS_COLORS[record.status] === 'warning' ? 'warning.main' : 'info.main', fontSize: 14, color: 'white' }}>
                    <Gavel sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{record.id}</Typography>
                    <Typography variant="caption" color="text.secondary">{getEmployeeName(employeeMap, record.employee?.id)}</Typography>
                  </Box>
                </Box>
                <Chip label={record.status} size="small" color={LIABILITY_STATUS_COLORS[record.status] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">{record.liabilityType}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{record.triggerDate}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Liability Records"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'warning.main' },
          { label: 'Pending', value: String(stats.pending), color: 'info.main' },
          { label: 'Disputed', value: String(stats.disputed), color: 'error.main' },
          { label: 'Resolved', value: String(stats.resolved), color: 'success.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="By Status"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Case #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Incident Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records?.map((record: LiabilityRecord) => (
              <TableRow
                key={record.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/liability-records/${record.id}/show`)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{record.id}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 12, color: 'white' }}>
                      {getEmployeeName(employeeMap, record.employee?.id)?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, record.employee?.id)}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{resolveVendorName(record.vendor?.id)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{record.liabilityType}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{record.triggerDate}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={record.status} size="small" color={LIABILITY_STATUS_COLORS[record.status] as ChipColor} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={records?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!records || records.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Balance sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No liability records found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const LiabilityRecordFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search case number or employee" />
    <SelectInput source="status" choices={LIABILITY_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);
